import type { RumEvent } from '@datadog/browser-rum-slim';
import { datadogRum } from '@datadog/browser-rum-slim';

const msClarityUrls = /^https:\/\/.*\.clarity\.ms/;
const firmPublicAssetsS3Urls = /(.+)-firm-public-assets-seccl/;
const cssUrls = /\/_next\/static\/css\//;
const excludedResourceUrls = [msClarityUrls, firmPublicAssetsS3Urls, cssUrls];

const buildSkewErrorMessages = [
  'Invariant: attempted to hard navigate to the same URL',
  'Failed to load client build manifest',
];

const isBuildSkewErrorMessage = (message?: string): boolean =>
  buildSkewErrorMessages.some(buildSkewErrorMessage => message?.startsWith(buildSkewErrorMessage));

// Query parameters that may contain PII (client names, search terms)
// or sensitive tokens and must be redacted before sending to Datadog.
const SENSITIVE_PARAMS = ['query', 'name', 'token'];

export const stripSensitiveParams = (url: string): string => {
  try {
    const parsed = new URL(url);
    let modified = false;
    for (const param of SENSITIVE_PARAMS) {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, '[REDACTED]');
        modified = true;
      }
    }
    return modified ? parsed.toString() : url;
  } catch {
    return url;
  }
};

/**
 * Datadog `beforeSend` hook. Redacts sensitive query parameters from every URL an
 * event can carry (resource, view and failed-request error URLs) and drops browser
 * `report` errors, build skew errors and excluded resource requests.
 * Returns `false` to discard the event.
 */
export const sanitiseRumEvent = (event: RumEvent): boolean => {
  // Redact sensitive query parameters from resource URLs
  if (event.type === 'resource') {
    const url = event.resource.url;
    if (excludedResourceUrls.some(regex => regex.test(url))) {
      return false;
    }
    event.resource.url = stripSensitiveParams(url);
  }

  // Redact sensitive query parameters from view URLs
  if (event.type === 'view' && event.view.url) {
    event.view.url = stripSensitiveParams(event.view.url);
  }

  if (event.type === 'error') {
    // Failed network requests surface the request URL on the error event too.
    if (event.error?.resource?.url) {
      event.error.resource.url = stripSensitiveParams(event.error.resource.url);
    }

    if (event.error?.source === 'report') {
      // We are excluding error.source = 'report' events from page load failure monitoring because they primarily
      // represent browser-generated reports (e.g. CSP violations, deprecations, or third-party scripts blocked
      // by extensions) rather than application runtime failures. Including them inflates failure rates with
      // non-actionable noise and leads to false alerts. By removing these events, we ensure page load metrics
      // reflect genuine user-impacting errors (e.g. uncaught exceptions or failed API calls), improving signal
      // quality and reducing alert fatigue.
      return false;
    }

    // Next throws these when a tab on an old build cannot load files from the new build during a deploy.
    // Remove once old build files stay available to open tabs.
    if (isBuildSkewErrorMessage(event.error?.message)) {
      return false;
    }
  }
  return true;
};

export const injectDatadogRum = async () => {
  if (datadogRum.getInitConfiguration() !== undefined) {
    return;
  }

  const { env = 'staging' } = await fetch('/api/env').then(res => res.json());

  datadogRum.init({
    applicationId: '0e1ffa89-130e-48ee-8f3c-c4c31519638c',
    clientToken: 'pube62a2d255a95536371a1b80a982e1f7d',
    site: 'datadoghq.eu',
    service: 'platform-ui',
    env,
    version: process.env.NEXT_PUBLIC_PRO_VERSION ?? 'local',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask',
    enablePrivacyForActionName: true,
    allowedTracingUrls: [() => true],
    beforeSend: sanitiseRumEvent,
  });
};
